import { useEffect, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useGetPropertiesQuery } from '@/components/properties/services/propertyApi';
import type { PropertyEntity } from '@/types/property.types';
import { PropertyPickerRow } from './PropertyPickerRow';
import { CartButton } from './CartButton';

const PAGE_SIZE = 5;
const DEBOUNCE_MS = 250;

interface PropertyPickerViewProps {
  onAddToCart: (property: PropertyEntity) => void;
  onAddNewProperty: () => void;
  /** propId of property currently being added; null when none. */
  addingPropertyId: string | null;
  /** Set of propId values already in the cart. */
  propertyIdsInCart: ReadonlySet<string>;
  /** Number of items in the cart; drives the cart button badge. */
  cartCount: number;
}

export function PropertyPickerView({
  onAddToCart,
  onAddNewProperty,
  addingPropertyId,
  propertyIdsInCart,
  cartCount,
}: PropertyPickerViewProps) {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedSearch(searchInput.trim()),
      DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [searchInput]);

  // The properties endpoint expects `searchQuery=` for the
  // free-text address/property-ID match — see propertyListUtils
  // getFilterChain.
  const filterQuery = debouncedSearch
    ? `&searchQuery=${encodeURIComponent(debouncedSearch)}`
    : '';

  const { data, isFetching } = useGetPropertiesQuery({
    page: 0,
    size: PAGE_SIZE,
    filterQuery,
    sortQuery: '',
  });

  const properties = data?.content ?? [];
  const hasSearch = debouncedSearch.length > 0;
  const sectionLabel = hasSearch ? 'MATCHING PROPERTIES' : 'RECENT PROPERTIES';
  const showEmptySearch = hasSearch && !isFetching && properties.length === 0;
  const showEmptyAll = !hasSearch && !isFetching && properties.length === 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by Address or Property ID…"
            className="pl-9"
            aria-label="Search Properties"
          />
        </div>
        <CartButton count={cartCount} />
      </div>

      {!showEmptyAll && (
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {sectionLabel}
        </p>
      )}

      {isFetching && properties.length === 0 && (
        <div className="flex items-center justify-center py-6">
          <Spinner />
        </div>
      )}

      {showEmptySearch && (
        <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground">
          No matches. Add a new property below.
        </p>
      )}

      <div className="flex flex-col gap-2">
        {properties.map((property) => (
          <PropertyPickerRow
            key={property.propId}
            property={property}
            isAddingToCart={addingPropertyId === property.propId}
            isInCart={propertyIdsInCart.has(property.propId)}
            onAddToCart={onAddToCart}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={onAddNewProperty}
        className="flex items-center gap-3 rounded-md border border-border bg-card p-3 text-left transition-shadow hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-primary text-primary-foreground">
          <Plus className="h-5 w-5" />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-primary">
            Add a new property
          </span>
          <span className="block text-xs text-muted-foreground">
            Save it to your property list and continue.
          </span>
        </span>
      </button>
    </div>
  );
}
